import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ExternalLink,
  Calendar,
  Clock,
  TrendingUp,
  Users,
  Target,
  Code,
  Database,
  Cloud,
  Shield,
  Brain,
  Smartphone,
  Monitor,
  Zap
} from 'lucide-react';
import SEO from '../components/SEO';
import QivratechLogo from '../components/QivratechLogo';

const ProjectsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);

  const categories = [
    { id: 'all', name: 'All Projects', icon: <Target className="w-5 h-5" /> },
    { id: 'ai-ml', name: 'AI & Machine Learning', icon: <Brain className="w-5 h-5" /> },
    { id: 'cloud', name: 'Cloud & DevOps', icon: <Cloud className="w-5 h-5" /> },
    { id: 'web', name: 'Web Development', icon: <Monitor className="w-5 h-5" /> },
    { id: 'mobile', name: 'Mobile Apps', icon: <Smartphone className="w-5 h-5" /> },
    { id: 'enterprise', name: 'Enterprise Solutions', icon: <Database className="w-5 h-5" /> }
  ];

  const projects = [
    {
      id: 1,
      category: 'ai-ml',
      title: "AI-Powered Financial Risk Assessment Platform",
      client: "FinTech Startup",
      industry: "Financial Services",
      duration: "8 months",
      teamSize: "6 engineers",
      status: "Completed",
      thumbnail: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80",
      description: "Developed an intelligent risk assessment system using machine learning algorithms to analyze creditworthiness and predict default rates for loan applications.",
      challenge: "The client needed to process thousands of loan applications daily while maintaining high accuracy in risk assessment and reducing manual review time by 70%.",
      solution: "Built a comprehensive ML platform with ensemble models, real-time data processing, and automated decision workflows that improved accuracy while reducing processing time.",
      techStack: {
        frontend: ["React", "TypeScript", "Material-UI", "D3.js"],
        backend: ["Python", "FastAPI", "PostgreSQL", "Redis"],
        ml: ["TensorFlow", "Scikit-learn", "XGBoost", "Pandas"],
        infrastructure: ["AWS", "Docker", "Kubernetes", "Jenkins"],
        monitoring: ["Prometheus", "Grafana", "ELK Stack"]
      },
      keyFeatures: [
        "Real-time credit scoring with 95% accuracy",
        "Automated document verification using OCR",
        "Fraud detection with anomaly detection algorithms",
        "Interactive risk visualization dashboard",
        "API-first architecture for easy integration"
      ],
      results: [
        { metric: "Processing Time", improvement: "70% reduction", icon: <Clock className="w-5 h-5" /> },
        { metric: "Accuracy Rate", improvement: "95% precision", icon: <Target className="w-5 h-5" /> },
        { metric: "Cost Savings", improvement: "$2.3M annually", icon: <TrendingUp className="w-5 h-5" /> },
        { metric: "Loan Approvals", improvement: "300% faster", icon: <Zap className="w-5 h-5" /> }
      ],
      testimonial: {
        quote: "Qivra's AI solution transformed our lending process. We now process 10x more applications with better accuracy and significantly reduced risk.",
        author: "Sarah Chen",
        position: "CTO, FinanceFlow"
      },
      architecture: "Microservices architecture with event-driven design, deployed on AWS EKS with auto-scaling capabilities and multi-region disaster recovery.",
      challenges: [
        "Handling sensitive financial data with strict compliance requirements",
        "Processing large volumes of unstructured data from multiple sources",
        "Ensuring real-time performance while maintaining high accuracy",
        "Building explainable AI models for regulatory compliance"
      ]
    },
    {
      id: 2,
      category: 'cloud',
      title: "Healthcare Cloud Migration & Modernization",
      client: "Regional Hospital Network",
      industry: "Healthcare",
      duration: "12 months",
      teamSize: "8 engineers",
      status: "Completed",
      thumbnail: "/images/projects/healthcare-cloud.png",
      description: "Migrated legacy healthcare systems to a modern cloud-native architecture, ensuring HIPAA compliance and improving system reliability.",
      challenge: "Legacy on-premise systems were causing frequent downtime, security vulnerabilities, and inability to scale during peak patient loads.",
      solution: "Designed and implemented a cloud-native architecture with microservices, automated CI/CD pipelines, and comprehensive monitoring for 99.9% uptime.",
      techStack: {
        cloud: ["AWS", "Lambda", "RDS", "S3", "CloudFormation"],
        containers: ["Docker", "Kubernetes", "EKS", "Helm"],
        cicd: ["GitLab CI", "Terraform", "Ansible", "ArgoCD"],
        monitoring: ["DataDog", "AWS CloudWatch", "PagerDuty"],
        security: ["AWS IAM", "Vault", "AWS KMS", "GuardDuty"]
      },
      keyFeatures: [
        "HIPAA-compliant cloud infrastructure",
        "Auto-scaling patient management system",
        "Disaster recovery with 99.9% uptime",
        "Secure API gateway for third-party integrations",
        "Real-time patient data synchronization"
      ],
      results: [
        { metric: "System Uptime", improvement: "99.9% availability", icon: <Shield className="w-5 h-5" /> },
        { metric: "Performance", improvement: "60% faster response", icon: <Zap className="w-5 h-5" /> },
        { metric: "Cost Reduction", improvement: "45% lower TCO", icon: <TrendingUp className="w-5 h-5" /> },
        { metric: "Deployment Speed", improvement: "90% faster releases", icon: <Clock className="w-5 h-5" /> }
      ],
      testimonial: {
        quote: "The cloud migration exceeded our expectations. Our patient systems are now more reliable and secure than ever before.",
        author: "Dr. Michael Rodriguez",
        position: "Chief Information Officer"
      },
      architecture: "Event-driven microservices architecture with API Gateway, Lambda functions, and managed databases, all deployed using Infrastructure as Code.",
      challenges: [
        "Zero-downtime migration of critical patient data systems",
        "Ensuring HIPAA compliance throughout the migration process",
        "Integrating with legacy medical equipment and third-party systems",
        "Training medical staff on new cloud-based workflows"
      ]
    },
    {
      id: 3,
      category: 'web',
      title: "E-commerce Platform with AI Recommendations",
      client: "Retail Fashion Brand",
      industry: "E-commerce",
      duration: "6 months",
      teamSize: "5 engineers",
      status: "Completed",
      thumbnail: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
      description: "Built a modern e-commerce platform with AI-powered product recommendations and real-time inventory management.",
      challenge: "Outdated e-commerce platform with poor user experience was resulting in high cart abandonment rates and low customer retention.",
      solution: "Developed a fast, responsive platform with personalized shopping experiences, real-time recommendations, and seamless checkout process.",
      techStack: {
        frontend: ["Next.js", "React", "TypeScript", "Tailwind CSS"],
        backend: ["Node.js", "Express", "MongoDB", "Redis"],
        ai: ["Python", "TensorFlow.js", "Collaborative Filtering"],
        payments: ["Stripe", "PayPal", "Apple Pay", "Google Pay"],
        analytics: ["Google Analytics", "Mixpanel", "Hotjar"]
      },
      keyFeatures: [
        "AI-powered product recommendations",
        "Real-time inventory tracking",
        "Progressive Web App (PWA) capabilities",
        "Multi-payment gateway integration",
        "Advanced search and filtering"
      ],
      results: [
        { metric: "Conversion Rate", improvement: "35% increase", icon: <TrendingUp className="w-5 h-5" /> },
        { metric: "Page Load Speed", improvement: "65% faster", icon: <Zap className="w-5 h-5" /> },
        { metric: "Mobile Sales", improvement: "120% growth", icon: <Smartphone className="w-5 h-5" /> },
        { metric: "Customer Retention", improvement: "40% improvement", icon: <Users className="w-5 h-5" /> }
      ],
      testimonial: {
        quote: "Our new platform has revolutionized our online presence. Sales have doubled since launch and customer satisfaction is at an all-time high.",
        author: "Emma Thompson",
        position: "Digital Marketing Director"
      },
      architecture: "JAMstack architecture with serverless functions, CDN distribution, and headless CMS for optimal performance and scalability.",
      challenges: [
        "Migrating 50,000+ products without data loss",
        "Implementing real-time inventory sync across multiple warehouses",
        "Creating personalized experiences without compromising page speed",
        "Ensuring seamless mobile shopping experience"
      ]
    },
    {
      id: 4,
      category: 'mobile',
      title: "IoT Fleet Management Mobile Application",
      client: "Logistics Company",
      industry: "Transportation & Logistics",
      duration: "7 months",
      teamSize: "4 engineers",
      status: "Completed",
      thumbnail: "/images/projects/fleet-management.png",
      description: "Developed a comprehensive mobile application for real-time fleet tracking, route optimization, and predictive maintenance.",
      challenge: "Manual fleet management processes were inefficient, leading to increased fuel costs, vehicle downtime, and poor customer service.",
      solution: "Created an integrated mobile solution with IoT sensors, real-time tracking, AI-powered route optimization, and predictive maintenance alerts.",
      techStack: {
        mobile: ["React Native", "TypeScript", "Redux Toolkit"],
        backend: ["Node.js", "Express", "PostgreSQL", "InfluxDB"],
        iot: ["MQTT", "AWS IoT Core", "Time Series Database"],
        maps: ["Google Maps API", "HERE Technologies"],
        realtime: ["WebSocket", "Server-Sent Events", "Redis Pub/Sub"]
      },
      keyFeatures: [
        "Real-time GPS tracking and geofencing",
        "AI-powered route optimization",
        "Predictive maintenance alerts",
        "Driver behavior monitoring",
        "Fuel efficiency analytics"
      ],
      results: [
        { metric: "Fuel Costs", improvement: "25% reduction", icon: <TrendingUp className="w-5 h-5" /> },
        { metric: "Vehicle Downtime", improvement: "40% decrease", icon: <Clock className="w-5 h-5" /> },
        { metric: "Delivery Times", improvement: "30% faster", icon: <Zap className="w-5 h-5" /> },
        { metric: "Customer Satisfaction", improvement: "50% increase", icon: <Users className="w-5 h-5" /> }
      ],
      testimonial: {
        quote: "The fleet management app has transformed our operations. We now have complete visibility into our vehicles and can make data-driven decisions.",
        author: "James Wilson",
        position: "Operations Manager"
      },
      architecture: "Cross-platform mobile architecture with real-time data synchronization, offline capabilities, and cloud-based analytics platform.",
      challenges: [
        "Integrating with diverse IoT sensors and telematics devices",
        "Ensuring reliable connectivity in remote areas",
        "Processing and analyzing large volumes of real-time sensor data",
        "Creating intuitive mobile interfaces for drivers and dispatchers"
      ]
    },
    {
      id: 5,
      category: 'enterprise',
      title: "Enterprise Resource Planning (ERP) System",
      client: "Manufacturing Corporation",
      industry: "Manufacturing",
      duration: "14 months",
      teamSize: "10 engineers",
      status: "In Progress",
      thumbnail: "/images/projects/erp-system.png",
      description: "Building a comprehensive ERP system to streamline manufacturing operations, supply chain management, and financial reporting.",
      challenge: "Disparate legacy systems across departments were creating data silos, inefficiencies, and reporting inconsistencies.",
      solution: "Developing an integrated ERP platform with unified data models, automated workflows, and real-time business intelligence dashboards.",
      techStack: {
        frontend: ["React", "TypeScript", "Ant Design", "Chart.js"],
        backend: ["Java", "Spring Boot", "PostgreSQL", "Apache Kafka"],
        integration: ["Apache Camel", "REST APIs", "GraphQL"],
        analytics: ["Apache Spark", "Elasticsearch", "Tableau"],
        security: ["OAuth 2.0", "JWT", "Role-based Access Control"]
      },
      keyFeatures: [
        "Unified inventory management system",
        "Automated procurement workflows",
        "Real-time production monitoring",
        "Financial reporting and analytics",
        "Supply chain optimization tools"
      ],
      results: [
        { metric: "Data Accuracy", improvement: "95% improvement", icon: <Target className="w-5 h-5" /> },
        { metric: "Process Efficiency", improvement: "50% faster", icon: <Zap className="w-5 h-5" /> },
        { metric: "Reporting Time", improvement: "80% reduction", icon: <Clock className="w-5 h-5" /> },
        { metric: "Integration Points", improvement: "15 systems unified", icon: <Database className="w-5 h-5" /> }
      ],
      testimonial: {
        quote: "The ERP system is unifying our operations like never before. We finally have a single source of truth for all our business data.",
        author: "Robert Chen",
        position: "Chief Operations Officer"
      },
      architecture: "Modular microservices architecture with event sourcing, CQRS patterns, and distributed caching for high-performance operations.",
      challenges: [
        "Integrating 15+ legacy systems without disrupting operations",
        "Handling complex manufacturing workflows and compliance requirements",
        "Ensuring data consistency across multiple business units",
        "Training 500+ users across different departments and locations"
      ]
    },
    {
      id: 6,
      category: 'web',
      title: "Homestay Booking Platform",
      client: "Independent Hospitality Group",
      industry: "Hospitality & Tourism",
      duration: "4 months",
      teamSize: "3 engineers",
      status: "Completed",
      thumbnail: "/images/projects/homestay.png",
      description: "A comprehensive booking platform for boutique homestays, featuring real-time availability, secure payments, and immersive property showcases.",
      challenge: "The client relied on manual phone bookings and spreadsheets, leading to double bookings, lost revenue, and an inability to attract international travelers.",
      solution: "Built a custom, high-performance booking engine with an intuitive admin dashboard, calendar synchronization, and seamless payment integration.",
      techStack: {
        frontend: ["React", "Next.js", "Tailwind CSS", "Framer Motion"],
        backend: ["Node.js", "Express", "MongoDB", "Redis"],
        payments: ["Stripe", "PayPal"],
        services: ["Cloudinary", "SendGrid", "Google Maps API"]
      },
      keyFeatures: [
        "Real-time availability calendar",
        "Secure global payment processing",
        "Immersive photo galleries & virtual tours",
        "Admin dashboard for reservation management",
        "Automated booking confirmations & reminders"
      ],
      results: [
        { metric: "Direct Bookings", improvement: "150% increase", icon: <TrendingUp className="w-5 h-5" /> },
        { metric: "Admin Time", improvement: "80% reduction", icon: <Clock className="w-5 h-5" /> },
        { metric: "Revenue", improvement: "2x growth", icon: <Zap className="w-5 h-5" /> },
        { metric: "User Experience", improvement: "5-star rating", icon: <Users className="w-5 h-5" /> }
      ],
      testimonial: {
        quote: "This platform transformed our business. We went from chaotic phone calls to fully automated bookings, and our guests love the smooth experience.",
        author: "Sarah Jenkins",
        position: "Property Owner"
      },
      architecture: "Serverless-first architecture using Next.js API routes and MongoDB Atlas for scalability and low maintenance.",
      challenges: [
        "Handling complex pricing rules (seasonal, weekend, long-stay discounts)",
        "Syncing availability across multiple platforms (Airbnb, Booking.com) via iCal",
        "Optimizing high-resolution image delivery for fast load times"
      ]
    }
  ];

  const filteredProjects = selectedCategory === 'all'
    ? projects
    : projects.filter(project => project.category === selectedCategory);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      <SEO
        title="Projects & Case Studies - Qivra Technologies | AI & IT Solutions"
        description="Explore Qivra Technologies' successful AI, machine learning, cloud computing, and software development projects. Intelligence unleashed for enterprise transformation."
        keywords="AI projects, machine learning case studies, cloud migration, software development, digital transformation, enterprise solutions, Qivra Technologies"
      />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-950 to-black">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `
                radial-gradient(circle at 25% 25%, rgba(34, 211, 238, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 75% 75%, rgba(168, 85, 247, 0.1) 0%, transparent 50%),
                radial-gradient(circle, rgba(14, 165, 233, 0.05) 1px, transparent 1px)
              `,
              backgroundSize: '100% 100%, 100% 100%, 60px 60px'
            }}
          />

          {/* Floating Logo Elements */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Top Area */}
            <motion.div
              className="absolute top-16 left-8 opacity-6"
              animate={{
                y: [0, -28, 0],
                x: [0, 16, 0],
                rotate: [0, 3, 0]
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <QivratechLogo variant="mark" size="default" theme="dark" />
            </motion.div>

            <motion.div
              className="absolute top-28 right-1/3 opacity-7"
              animate={{
                y: [0, -25, 0],
                x: [0, 12, 0],
                rotate: [0, 4, 0]
              }}
              transition={{
                duration: 11,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2
              }}
            >
              <QivratechLogo variant="mark" size="large" theme="dark" />
            </motion.div>

            {/* Middle Area */}
            <motion.div
              className="absolute top-1/2 right-10 opacity-5"
              animate={{
                y: [0, -26, 0],
                x: [0, -18, 0],
                rotate: [0, -6, 0]
              }}
              transition={{
                duration: 12,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 5
              }}
            >
              <QivratechLogo variant="mark" size="small" theme="dark" />
            </motion.div>

            {/* Bottom Area */}
            <motion.div
              className="absolute bottom-28 left-20 opacity-8"
              animate={{
                y: [0, -26, 0],
                x: [0, 20, 0],
                rotate: [0, 7, 0]
              }}
              transition={{
                duration: 12,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 6
              }}
            >
              <QivratechLogo variant="mark" size="default" theme="dark" />
            </motion.div>

            <motion.div
              className="absolute bottom-16 right-8 opacity-6"
              animate={{
                y: [0, -34, 0],
                x: [0, -18, 0],
                rotate: [0, -6, 0]
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 8
              }}
            >
              <QivratechLogo variant="mark" size="large" theme="dark" />
            </motion.div>
          </div>
        </div>

        <motion.div
          className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 px-4 py-2 rounded-full border border-cyan-400/30 mb-8">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
              <span className="text-cyan-400 text-sm font-light tracking-wide">Our Success Stories</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight mb-8 leading-tight tracking-wide">
              <span className="text-white">Project</span>
              <br />
              <span className="bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-500 bg-clip-text text-transparent font-light">
                Portfolio
              </span>
            </h1>

            <p className="text-2xl text-gray-300 max-w-4xl mx-auto font-light leading-relaxed tracking-wide">
              Discover how we've transformed businesses across industries with intelligent technology solutions
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Projects Section */}
      <section className="py-32 bg-gradient-to-b from-black via-gray-950 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 px-4 py-2 rounded-full border border-cyan-400/30 mb-8">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
              <span className="text-cyan-400 text-sm font-light tracking-wide">Real Results, Real Impact</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-extralight mb-8 leading-tight tracking-wide text-white">
              Case Studies
            </h2>

            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-light leading-relaxed tracking-wide">
              Explore our successful projects and transformative solutions across industries
            </p>
          </motion.div>

          {/* Category Filters */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-4 mb-16"
          >
            {categories.map((category) => (
              <motion.button
                key={category.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${selectedCategory === category.id
                  ? 'bg-gradient-to-r from-cyan-400 to-purple-600 text-white shadow-lg shadow-cyan-400/50'
                  : 'bg-gray-800/50 text-gray-300 hover:text-cyan-400 border border-gray-700 hover:border-cyan-400/50'
                  }`}
              >
                {category.icon}
                <span>{category.name}</span>
              </motion.button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            key={selectedCategory}
          >
            {filteredProjects.map((project, idx) => (
              <motion.div
                key={project.id}
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                onClick={() => setSelectedProject(project)}
                className="group bg-gradient-to-br from-gray-900 to-black rounded-3xl overflow-hidden border border-gray-800 hover:border-cyan-400/50 transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.thumbnail}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${project.status === 'Completed'
                      ? 'bg-green-500 text-white'
                      : 'bg-yellow-500 text-black'
                      }`}>
                      {project.status}
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4">
                    <span className="bg-gradient-to-r from-cyan-400 to-purple-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                      {project.industry}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-400 transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-gray-400 mb-4 line-clamp-3">
                    {project.description}
                  </p>



                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-2">
                      {Object.values(project.techStack)[0]?.slice(0, 3).map((tech, techIdx) => (
                        <span
                          key={techIdx}
                          className="bg-cyan-500/20 text-cyan-300 px-2 py-1 rounded text-xs"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                    <ArrowRight className="w-5 h-5 text-cyan-400 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Project Details Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-gradient-to-br from-gray-900 to-black rounded-3xl border border-cyan-400/30 max-w-6xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <img
                  src={selectedProject.thumbnail}
                  alt={selectedProject.title}
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                <button
                  onClick={() => setSelectedProject(null)}
                  className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm text-white p-2 rounded-full hover:bg-black/70 transition-all"
                >
                  ✕
                </button>
              </div>

              <div className="p-8">
                <div className="grid lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <h2 className="text-3xl font-black text-white mb-4">{selectedProject.title}</h2>
                    <p className="text-lg text-gray-300 mb-6">{selectedProject.description}</p>

                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-cyan-400 mb-4">Challenge</h3>
                      <p className="text-gray-300 mb-6">{selectedProject.challenge}</p>

                      <h3 className="text-xl font-bold text-cyan-400 mb-4">Solution</h3>
                      <p className="text-gray-300 mb-6">{selectedProject.solution}</p>
                    </div>

                    <div className="mb-8">
                      <h3 className="text-xl font-bold text-cyan-400 mb-4">Key Features</h3>
                      <div className="grid gap-3">
                        {selectedProject.keyFeatures.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg">
                            <div className="w-2 h-2 bg-cyan-400 rounded-full" />
                            <span className="text-gray-300">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-cyan-400 mb-4">Architecture & Challenges</h3>
                      <p className="text-gray-300 mb-4">{selectedProject.architecture}</p>
                      <div className="grid gap-2">
                        {selectedProject.challenges.map((challenge, idx) => (
                          <div key={idx} className="flex items-start gap-3 text-sm text-gray-400">
                            <span className="text-yellow-400 mt-1">•</span>
                            <span>{challenge}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="lg:col-span-1">
                    <div className="bg-gradient-to-br from-cyan-500/10 to-purple-600/10 p-6 rounded-2xl border border-cyan-400/30 mb-6">
                      <h3 className="text-lg font-bold text-white mb-4">Project Details</h3>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Client:</span>
                          <span className="text-white">{selectedProject.client}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Industry:</span>
                          <span className="text-white">{selectedProject.industry}</span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-400">Status:</span>
                          <span className={`font-bold ${selectedProject.status === 'Completed' ? 'text-green-400' : 'text-yellow-400'
                            }`}>
                            {selectedProject.status}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-lg font-bold text-white mb-4">Results & Impact</h3>
                      <div className="grid gap-4">
                        {selectedProject.results.map((result, idx) => (
                          <div key={idx} className="bg-gray-800/30 p-4 rounded-lg border border-gray-700">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="text-cyan-400">{result.icon}</div>
                              <span className="text-sm text-gray-400">{result.metric}</span>
                            </div>
                            <div className="text-lg font-bold text-white">{result.improvement}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-lg font-bold text-white mb-4">Technology Stack</h3>
                      <div className="space-y-4">
                        {Object.entries(selectedProject.techStack).map(([category, technologies]) => (
                          <div key={category}>
                            <h4 className="text-sm font-semibold text-cyan-400 mb-2 capitalize">{category}</h4>
                            <div className="flex flex-wrap gap-2">
                              {technologies.map((tech, idx) => (
                                <span key={idx} className="bg-gray-700/50 text-gray-300 px-2 py-1 rounded text-xs">
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {selectedProject.testimonial && (
                      <div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 p-6 rounded-2xl border border-purple-400/30">
                        <div className="text-3xl text-purple-400 mb-3">"</div>
                        <p className="text-gray-300 italic mb-4">{selectedProject.testimonial.quote}</p>
                        <div className="text-sm">
                          <div className="font-bold text-white">{selectedProject.testimonial.author}</div>
                          <div className="text-purple-400">{selectedProject.testimonial.position}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-b from-gray-950 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 px-4 py-2 rounded-full border border-cyan-400/30 mb-8">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
              <span className="text-cyan-400 text-sm font-light tracking-wide">Start Your Journey</span>
            </div>

            <h2 className="text-4xl md:text-5xl font-extralight mb-8 leading-tight tracking-wide text-white">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-gray-300 mb-12 font-light leading-relaxed tracking-wide">
              Let's discuss how we can help you achieve similar results with our proven approach and expertise
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link
                to="/contact"
                className="group bg-gradient-to-r from-cyan-400/10 to-purple-600/10 border border-cyan-400/30 px-8 py-4 rounded-2xl font-light tracking-wide text-white hover:bg-gradient-to-r hover:from-cyan-400/20 hover:to-purple-600/20 hover:border-cyan-400/60 transition-all duration-300 flex items-center gap-2 justify-center backdrop-blur-sm"
              >
                <span>Talk to an Expert</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <Link
                to="/solutions"
                className="group bg-gradient-to-r from-purple-400/10 to-cyan-600/10 border border-purple-400/30 px-8 py-4 rounded-2xl font-light tracking-wide text-purple-300 hover:bg-gradient-to-r hover:from-purple-400/20 hover:to-cyan-600/20 hover:border-purple-400/60 hover:text-white transition-all duration-300 backdrop-blur-sm"
              >
                View Our Solutions
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;